import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';

import Menu, { MenuItem } from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import IconButton from 'components/common/IconButton';
import IconCheck from 'components/icons/IconCheck';
import IconVisa from 'components/icons/IconVisa';
import IconMasterCard from 'components/icons/IconMasterCard';
import IconAmericanExpress from 'components/icons/IconAmericanExpress';
import IconMaestro from 'components/icons/IconMaestro';
import IconDiscover from 'components/icons/IconDiscover';
import IconMoreVert from 'components/icons/IconMoreVert';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';

import {
  updatePatientCreditCard,
  deletePatientCreditCard,
} from 'redux/modules/patientsCreditCards';
import splitLongWords from 'utils/splitLongWords';

const styles = theme => ({
  tableRow: {
    '&:last-child td': {
      borderBottom: 'none',
    },
  },
  tableCellRoot: {
    fontSize: pxToRem(13),
    letterSpacing: pxToEm(0.5, 13),
  },
  tableCellRootNoWrap: {
    whiteSpace: 'nowrap',
  },
  tableCellButton: {
    padding: '0 12px 0 10px',
  },
  caption: {
    display: 'inline-block',
    verticalAlign: 'middle',
    fontSize: pxToRem(13),
  },
  tabletUpOnly: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  iconCheck: {
    display: 'inline-block',
    width: 18,
    height: 14,
    marginRight: 10,
    verticalAlign: 'middle',
    color: theme.palette.primary[500],
  },
  iconCard: {
    width: 32,
    height: 18,
    marginRight: 10,
    verticalAlign: 'middle',
  },
  iconCardPlaceholder: {
    position: 'relative',
    display: 'inline-block',
    borderRadius: 2,
    backgroundColor: theme.palette.primary.A400,
    '&:before': {
      position: 'absolute',
      content: "''",
      display: 'block',
      top: 3,
      left: 0,
      right: 0,
      height: 4,
      backgroundColor: theme.palette.common.white,
    },
    '&:after': {
      position: 'absolute',
      content: "''",
      display: 'block',
      bottom: 4,
      right: 4,
      height: 4,
      width: 6,
      backgroundColor: theme.palette.common.white,
    },
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
});

class CreditCardsItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      deleteMenuOpen: false,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onSetDefaultMenuItemClick = this.onSetDefaultMenuItemClick.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
  }

  onMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      deleteMenuOpen: false,
    });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onSetDefaultMenuItemClick() {
    this.props.actions.updatePatientCreditCard({
      values: {
        'User Record Number': this.props['Record Number'],
        ID: this.props.ID,
        'A default payment method': true,
      },
    });
    this.setState({ anchorEl: null });
  }

  onDeleteMenuItemClick() {
    this.setState({ deleteMenuOpen: true });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    this.props.actions.deletePatientCreditCard({
      'Record Number': this.props['Record Number'],
      ID: this.props.ID,
    });
    this.setState({ anchorEl: null });
  }

  render() {
    const { account, classes } = this.props;
    const open = Boolean(this.state.anchorEl);
    let CardIcon;
    switch (this.props['Card Type']) {
      case 'VISA':
        CardIcon = (
          <IconVisa
            className={classes.iconCard}
          />
        );
        break;
      case 'MasterCard':
        CardIcon = (
          <IconMasterCard
            className={classes.iconCard}
          />
        );
        break;
      case 'Amex':
        CardIcon = (
          <IconAmericanExpress
            className={classes.iconCard}
          />
        );
        break;
      case 'Maestro':
        CardIcon = (
          <IconMaestro
            className={classes.iconCard}
          />
        );
        break;
      case 'Discover':
        CardIcon = (
          <IconDiscover
            className={classes.iconCard}
          />
        );
        break;
      default:
        CardIcon = (
          <div className={classNames(classes.iconCard, classes.iconCardPlaceholder)} />
        );
    }

    return (
      <TableRow className={classes.tableRow}>
        <TableCell
          className={classNames(classes.tableCellRoot, classes.tableCellRootNoWrap)}
        >

          {CardIcon}
          <Typography
            className={classes.caption}
          >
            {`
              Card
              ${String.fromCharCode(160)}
              **** **** ${this.props['Card Number'].slice(-4)}
            `}
          </Typography>
        </TableCell>
        <TableCell
          className={classes.tableCellRoot}
        >
          {splitLongWords(this.props['Name on Card'])}
        </TableCell>
        <TableCell
          className={classNames(classes.tableCellRoot, classes.tableCellRootNoWrap)}
        >
          {this.props['A default payment method'] === true &&
            <Fragment>
              <IconCheck
                className={classes.iconCheck}
              />
              <Typography
                color="secondary"
                className={classNames(classes.caption, classes.tabletUpOnly)}
              >
                Default Payment Method
              </Typography>
            </Fragment>
          }
        </TableCell>
        <TableCell
          className={classNames(classes.tableCellRoot, classes.tableCellButton)}
          padding="none"
          numeric
        >
          <IconButton
            aria-label="More"
            aria-owns={open ? `${this.props['Card Number']}-credit-card-menu` : null}
            aria-haspopup="true"
            onClick={this.onMenuButtonClick}
            tooltipContent="Open Menu"
          >
            <IconMoreVert
              className={classes.iconMoreVert}
            />
          </IconButton>
          <Menu
            id={`${this.props['Card Number']}-credit-card-menu`}
            anchorEl={this.state.anchorEl}
            open={open}
            onClose={this.onMenuClose}
            list={!this.state.deleteMenuOpen}
          >
            {this.state.deleteMenuOpen ? (
              <DeleteMenu
                text="Are you sure you want to delete this credit card?"
                onCancel={this.onDeleteCancel}
                onSubmit={this.onDeleteSubmit}
              />
            ) : (
              <Fragment>
                <MenuItem
                  onClick={this.onSetDefaultMenuItemClick}
                >
                  Set default
                </MenuItem>
                <MenuItem
                  color="warning"
                  onClick={this.onDeleteMenuItemClick}
                  disabled={account.Roles.filter(item =>
                    item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                >
                  Delete
                </MenuItem>
              </Fragment>
            )}
          </Menu>
        </TableCell>
      </TableRow>
    );
  }
}

CreditCardsItem.propTypes = {
  ID: PropTypes.number.isRequired,
  'Record Number': PropTypes.number.isRequired,
  'Card Number': PropTypes.string.isRequired,
  'Name on Card': PropTypes.string.isRequired,
  'A default payment method': PropTypes.bool.isRequired,
  'Card Type': PropTypes.string,
  actions: PropTypes.shape({
    updatePatientCreditCard: PropTypes.func.isRequired,
    deletePatientCreditCard: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    tableRow: PropTypes.string.isRequired,
    tableCellRoot: PropTypes.string.isRequired,
    tableCellRootNoWrap: PropTypes.string.isRequired,
    tableCellButton: PropTypes.string.isRequired,
    caption: PropTypes.string.isRequired,
    iconCheck: PropTypes.string.isRequired,
    iconCard: PropTypes.string.isRequired,
    iconCardPlaceholder: PropTypes.string.isRequired,
    iconMoreVert: PropTypes.string.isRequired,
  }).isRequired,
};

CreditCardsItem.defaultProps = {
  'Card Type': null,
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    updatePatientCreditCard,
    deletePatientCreditCard,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(CreditCardsItem),
);



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/CreditCards/CreditCardsItem.js